import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { useNavigate } from 'react-router-dom';
import { JSX } from 'react';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { PermitTypeListDTO } from './PermitTypes.tsx';
import { AutoActive } from 'product-shared/product-template/ProductOptions.tsx';

const PermitTypeItem = ({
    permit,
    showAccount,
    onRowSelect,
}: {
    permit: PermitTypeListDTO;
    showAccount: boolean;
    onRowSelect: () => void;
}): JSX.Element => {
    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={onRowSelect}>
            <TableCell>{permit.permitTypeDescription}</TableCell>
            <TableCell>{EmDash}</TableCell>
            <TableCell>
                {permit.zoneSelectionType === 'fixed' ? (
                    <>
                        {permit.zoneCount}{' '}
                        <Localized
                            de="FIXE"
                            fr="FIXE"
                            it="FISSA/E"
                            en="FIXED"
                        />
                    </>
                ) : (
                    <>
                        {permit.zoneSelectionType === 'single_choice'
                            ? '1'
                            : 'x'}{' '}
                        <Localized
                            de="WÄHLBARE"
                            fr="AU CHOIX"
                            it="A SCELTA"
                            en="SELECTABLE"
                        />
                    </>
                )}
            </TableCell>
            <TableCell>
                {(permit.autoActive === AutoActive.ALL_CHANNELS ||
                    permit.autoActive ===
                        AutoActive.ALL_CHANNELS_BUT_WHITELIST) && (
                    <>
                        <Localized
                            de="ONLINE"
                            fr="EN LIGNE"
                            it="ONLINE"
                            en="ONLINE"
                        />
                        {' / '}
                    </>
                )}
                <Localized
                    de="SCHALTER"
                    fr="GUICHET"
                    it="SPORTELLO"
                    en="COUNTER"
                />
            </TableCell>
            <TableCell>
                {permit.needsClearance ? (
                    <Localized de="Ja" fr="Oui" it="Sì" en="Yes" />
                ) : (
                    <Localized de="Nein" fr="Non" it="No" en="No" />
                )}
            </TableCell>
            {showAccount && (
                <TableCell>{permit.dtaAccountDescription ?? EmDash}</TableCell>
            )}
        </TableRow>
    );
};

export function PermitTypesList({ permits }: { permits: PermitTypeListDTO[] }) {
    const navigate = useNavigate();
    const showAccount = permits[0]?.showAccount ?? false;
    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Bewilligungsart"
                                fr="Type d'autorisation"
                                it="Tipo di autorizzazione"
                                en="Type of authorisation"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Profil Nr"
                                fr="N° du profil"
                                it="Nr. profilo"
                                en="Profile no."
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Zonen"
                                fr="Zones"
                                it="Zone"
                                en="Zones"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Verkaufsart"
                                fr="Type de vente"
                                it="Tipo di vendita"
                                en="Type of Sale"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Freigabepflicht"
                                fr="Approbation obligatoire"
                                it="Approvazione obbligatoria"
                                en="Requirement for Approval"
                            />
                        </TableCell>
                        {showAccount ? (
                            <TableCell>
                                <Localized
                                    de="Gruppe"
                                    fr="Groupe"
                                    it="Gruppo"
                                    en="Group"
                                />
                            </TableCell>
                        ) : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {permits.map(permit => (
                        <PermitTypeItem
                            key={permit.permitTypeId}
                            permit={permit}
                            showAccount={showAccount}
                            onRowSelect={() =>
                                navigate(`${permit.permitTypeId}`)
                            }
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
